import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Btn, Input, Select, Field, Chip } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { Plus, X, PencilSimple, ToggleLeft, ToggleRight } from "@phosphor-icons/react";

const TABS = [
  { key: "clients", label: "Clients" },
  { key: "plate_sizes", label: "Plate Sizes" },
  { key: "dpis", label: "DPIs" },
  { key: "operators", label: "Operators" },
  { key: "production_units", label: "Production Units" },
  { key: "locations", label: "Workshops / Locations" },
  { key: "suppliers", label: "Suppliers" },
];

const emptyOf = {
  clients: { name: "", code: "", contact_person: "", mobile: "", email: "", billing_address: "", delivery_address: "", gst_number: "", active: true },
  machines: { name: "", code: "", active: true, notes: "" },
  plate_sizes: { width: 550, height: 700, unit: "mm", min_stock: 0, active: true },
  dpis: { value: 150, active: true },
  operators: { name: "", code: "", machine_id: "", mobile: "", active: true },
  production_units: { name: "", type: "internal", contact_person: "", mobile: "", address: "", active: true },
  locations: { name: "", type: "godown", address: "", active: true },
  suppliers: { name: "", contact_person: "", phone: "", email: "", address: "", gst_number: "", active: true },
};

export default function Masters() {
  const m = useMasters();
  const [tab, setTab] = useState("clients");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOf.clients);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [clientAccess, setClientAccess] = useState({ enabled: false, exists: false, login_id: "", password: "", active: true });

  // Reset selection when switching tabs
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const list = m[tab] || [];

  const openNew = () => { setEditing(null); setForm({ ...emptyOf[tab] }); setClientAccess({ enabled:false, exists:false, login_id:"", password:"", active:true }); setShowForm(true); };
  const openEdit = async (row) => {
    const base = { ...emptyOf[tab], ...row };
    if (tab === "operators" && row.machine_id) base.machine_id = row.machine_id;
    setEditing(row); setForm(base); setShowForm(true);
    if (tab === "clients") {
      try {
        const { data } = await api.get(`/client-accounts/${row.id}`);
        setClientAccess(data ? { enabled:true, exists:true, login_id:data.login_id, password:"", active:data.active !== false } : { enabled:false, exists:false, login_id:(row.code||"").toLowerCase(), password:"", active:true });
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const save = async () => {
    try {
      const url = `/masters/${tab}`;
      let saved;
      if (editing) { await api.patch(`${url}/${editing.id}`, form); saved = editing; }
      else { const response = await api.post(url, form); saved = response.data; }
      if (tab === "clients" && clientAccess.enabled && saved?.id) {
        await api.put(`/client-accounts/${saved.id}`, {
          login_id: clientAccess.login_id,
          password: clientAccess.password || null,
          active: clientAccess.active,
        });
      } else if (tab === "clients" && clientAccess.exists && saved?.id) {
        await api.put(`/client-accounts/${saved.id}`, { login_id: clientAccess.login_id, password: null, active: false });
      }
      toast.success(editing ? "Updated" : "Created");
      setShowForm(false); m.reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === list.length && list.length > 0) setSelected(new Set());
    else setSelected(new Set(list.map(r => r.id)));
  };

  const setActive = async (ids, active) => {
    if (!ids.length) return;
    const label = active ? "Activate" : "Deactivate";
    if (!window.confirm(`${label} ${ids.length} record${ids.length > 1 ? "s" : ""}?`)) return;
    setBusy(true);
    try {
      await api.post(`/masters/${tab}/set-active`, { ids, active });
      toast.success(`${label}d ${ids.length} record${ids.length > 1 ? "s" : ""}`);
      setSelected(new Set());
      m.reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const bulkDeactivate = () => setActive(Array.from(selected), false);
  const bulkActivate = () => setActive(Array.from(selected), true);
  const toggleOne = (row) => setActive([row.id], !row.active);

  const allSelected = list.length > 0 && selected.size === list.length;
  const someSelected = selected.size > 0 && selected.size < list.length;

  const colspan = useMemo(() => {
    const base = 3; // checkbox + status + actions
    if (tab === "clients") return base + 5;
    if (tab === "machines") return base + 2;
    if (tab === "plate_sizes") return base + 4;
    if (tab === "operators") return base + 4;
    if (tab === "production_units") return base + 3;
    if (tab === "locations") return base + 2;
    if (tab === "suppliers") return base + 3;
    if (tab === "dpis") return base + 1;
    return 10;
  }, [tab]);

  return (
    <div data-testid="masters-page">
      <Toaster position="bottom-right" />
      <PageHeader
        title="Master Data"
        subtitle="Manage clients, machines, plate sizes, operators, production units, locations & suppliers."
        actions={<Btn data-testid="masters-add-btn" onClick={openNew}><Plus size={14} /> Add {TABS.find(t=>t.key===tab)?.label.slice(0,-1)}</Btn>}
      />
      <div className="px-3 pt-3 sm:px-6 sm:pt-4">
        <div className="flex overflow-x-auto gap-1 border-b border-zinc-200">
          {TABS.map(t => (
            <button
              key={t.key}
              data-testid={`master-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 whitespace-nowrap px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab===t.key ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <div className="p-3 sm:p-6 min-w-0">
        {selected.size > 0 && (
          <div className="mb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-sm border border-zinc-300 bg-zinc-50 px-3 sm:px-4 py-2" data-testid="masters-bulk-toolbar">
            <div className="text-sm font-semibold text-zinc-800">
              <span data-testid="masters-selected-count">{selected.size}</span> selected
            </div>
            <div className="flex flex-wrap gap-2">
              <Btn variant="outline" onClick={() => setSelected(new Set())} data-testid="masters-clear-selection">Clear</Btn>
              <Btn variant="outline" onClick={bulkActivate} disabled={busy} data-testid="masters-bulk-activate">Activate Selected</Btn>
              <Btn variant="danger" onClick={bulkDeactivate} disabled={busy} data-testid="masters-bulk-deactivate">Deactivate Selected</Btn>
            </div>
          </div>
        )}
        <Card>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[780px] data-table" data-testid={`masters-${tab}-table`}>
            <thead>
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    data-testid="masters-select-all"
                    aria-label="Select all"
                  />
                </th>
                {tab === "clients" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Contact</th><th className="text-left px-3 py-2">Mobile</th><th className="text-left px-3 py-2">GST</th></>}
                {tab === "machines" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Code</th></>}
                {tab === "plate_sizes" && <><th className="text-left px-3 py-2">Canonical</th><th className="text-left px-3 py-2">Width</th><th className="text-left px-3 py-2">Height</th><th className="text-left px-3 py-2">Min Stock</th></>}
                {tab === "operators" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Machine</th><th className="text-left px-3 py-2">Mobile</th></>}
                {tab === "production_units" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Contact</th></>}
                {tab === "locations" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Type</th></>}
                {tab === "suppliers" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">GST</th></>}
                {tab === "dpis" && <><th className="text-left px-3 py-2">DPI Value</th></>}
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={colspan} className="text-center py-8 text-zinc-500 text-sm">No records</td></tr>}
              {list.map(row => (
                <tr key={row.id} data-testid={`masters-row-${row.id}`} className={selected.has(row.id) ? "bg-zinc-50" : ""}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleRow(row.id)}
                      data-testid={`masters-select-${row.id}`}
                      aria-label={`Select ${row.name || row.canonical || row.id}`}
                    />
                  </td>
                  {tab === "clients" && <><td className="px-3 py-2 font-semibold">{row.name}</td><td className="px-3 py-2 font-mono">{row.code||"-"}</td><td className="px-3 py-2">{row.contact_person||"-"}</td><td className="px-3 py-2 font-mono">{row.mobile||"-"}</td><td className="px-3 py-2 font-mono">{row.gst_number||"-"}</td></>}
                  {tab === "machines" && <><td className="px-3 py-2 font-semibold">{row.name}</td><td className="px-3 py-2 font-mono">{row.code}</td></>}
                  {tab === "plate_sizes" && <><td className="px-3 py-2 font-mono font-semibold">{row.canonical}</td><td className="px-3 py-2 font-mono">{row.width}</td><td className="px-3 py-2 font-mono">{row.height}</td><td className="px-3 py-2 font-mono">{row.min_stock}</td></>}
                  {tab === "operators" && <><td className="px-3 py-2 font-semibold">{row.name}</td><td className="px-3 py-2 font-mono">{row.code||"-"}</td><td className="px-3 py-2">{nameOf(m.machines, row.machine_id)}</td><td className="px-3 py-2 font-mono">{row.mobile||"-"}</td></>}
                  {tab === "production_units" && <><td className="px-3 py-2 font-semibold">{row.name}</td><td className="px-3 py-2"><Chip tone={row.type==='internal'?'info':'neutral'}>{row.type}</Chip></td><td className="px-3 py-2">{row.contact_person||"-"}</td></>}
                  {tab === "locations" && <><td className="px-3 py-2 font-semibold">{row.name}</td><td className="px-3 py-2">{row.type||"-"}</td></>}
                  {tab === "suppliers" && <><td className="px-3 py-2 font-semibold">{row.name}</td><td className="px-3 py-2 font-mono">{row.phone||"-"}</td><td className="px-3 py-2 font-mono">{row.gst_number||"-"}</td></>}
                  {tab === "dpis" && <><td className="px-3 py-2 font-mono font-semibold">{row.value}</td></>}
                  <td className="px-3 py-2"><Chip tone={row.active?"good":"neutral"}>{row.active?"Active":"Inactive"}</Chip></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Btn variant="ghost" data-testid={`masters-toggle-${row.id}`} onClick={() => toggleOne(row)} disabled={busy}>
                      {row.active ? <><ToggleRight size={14} /> Deactivate</> : <><ToggleLeft size={14} /> Activate</>}
                    </Btn>
                    <Btn variant="ghost" data-testid={`masters-edit-${row.id}`} onClick={() => openEdit(row)}><PencilSimple size={14} /> Edit</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-sm border border-zinc-200 w-full max-w-lg my-2 sm:my-0" onClick={(e) => e.stopPropagation()} data-testid="masters-form-dialog">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div className="font-heading font-bold">{editing ? "Edit" : "Add"} {TABS.find(t=>t.key===tab)?.label.slice(0,-1)}</div>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              {tab === "clients" && <>
                <Field label="Name*"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="client-form-name" /></Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Code"><Input value={form.code||""} onChange={e=>setForm({...form,code:e.target.value})} /></Field>
                  <Field label="Contact"><Input value={form.contact_person||""} onChange={e=>setForm({...form,contact_person:e.target.value})} /></Field>
                  <Field label="Mobile"><Input value={form.mobile||""} onChange={e=>setForm({...form,mobile:e.target.value})} /></Field>
                  <Field label="Email"><Input type="email" value={form.email||""} onChange={e=>setForm({...form,email:e.target.value})} /></Field>
                  <Field label="GST"><Input value={form.gst_number||""} onChange={e=>setForm({...form,gst_number:e.target.value})} /></Field>
                </div>
                <Field label="Billing Address"><Input value={form.billing_address||""} onChange={e=>setForm({...form,billing_address:e.target.value})} /></Field>
                <Field label="Delivery Address"><Input value={form.delivery_address||""} onChange={e=>setForm({...form,delivery_address:e.target.value})} /></Field>
                <div className="border-t border-zinc-200 pt-4 mt-4">
                  <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={clientAccess.enabled} onChange={e=>setClientAccess({...clientAccess,enabled:e.target.checked})} /> Enable client portal login</label>
                  {clientAccess.enabled && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <Field label="Client login ID"><Input value={clientAccess.login_id} onChange={e=>setClientAccess({...clientAccess,login_id:e.target.value})} /></Field>
                    <Field label={editing ? "New password (optional)" : "Password"}><Input type="password" value={clientAccess.password} onChange={e=>setClientAccess({...clientAccess,password:e.target.value})} /></Field>
                    <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={clientAccess.active} onChange={e=>setClientAccess({...clientAccess,active:e.target.checked})} /> Client can sign in</label>
                  </div>}
                </div>
              </>}
              {tab === "machines" && <>
                <Field label="Name*"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field>
                <Field label="Code*"><Input value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})} /></Field>
                <Field label="Notes"><Input value={form.notes||""} onChange={e=>setForm({...form,notes:e.target.value})} /></Field>
              </>}
              {tab === "plate_sizes" && <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Width*"><Input type="number" value={form.width} onChange={e=>setForm({...form,width:parseFloat(e.target.value)})} /></Field>
                  <Field label="Height*"><Input type="number" value={form.height} onChange={e=>setForm({...form,height:parseFloat(e.target.value)})} /></Field>
                  <Field label="Unit"><Input value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} /></Field>
                </div>
                <Field label="Min Stock"><Input type="number" value={form.min_stock} onChange={e=>setForm({...form,min_stock:parseInt(e.target.value||0)})} /></Field>
                <div className="text-xs text-zinc-500">Canonical: <span className="font-mono font-bold">{form.width}X{form.height}</span></div>
              </>}
              {tab === "operators" && <>
                <Field label="Name*"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Code"><Input value={form.code||""} onChange={e=>setForm({...form,code:e.target.value})} /></Field>
                  <Field label="Mobile"><Input value={form.mobile||""} onChange={e=>setForm({...form,mobile:e.target.value})} /></Field>
                </div>
                <Field label="Assigned Machine">
                  <Select value={form.machine_id||""} onChange={e=>setForm({...form,machine_id:e.target.value})}>
                    <option value="">— None —</option>
                    {m.machines.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                  </Select>
                </Field>
              </>}
              {tab === "production_units" && <>
                <Field label="Name*"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field>
                <Field label="Type*">
                  <Select value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                    <option value="internal">Internal Workshop</option>
                    <option value="vendor">Outsourced Vendor</option>
                  </Select>
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Contact"><Input value={form.contact_person||""} onChange={e=>setForm({...form,contact_person:e.target.value})} /></Field>
                  <Field label="Mobile"><Input value={form.mobile||""} onChange={e=>setForm({...form,mobile:e.target.value})} /></Field>
                </div>
                <Field label="Address"><Input value={form.address||""} onChange={e=>setForm({...form,address:e.target.value})} /></Field>
              </>}
              {tab === "locations" && <>
                <Field label="Name*"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field>
                <Field label="Type"><Input value={form.type||""} onChange={e=>setForm({...form,type:e.target.value})} placeholder="godown / workshop" /></Field>
                <Field label="Address"><Input value={form.address||""} onChange={e=>setForm({...form,address:e.target.value})} /></Field>
              </>}
              {tab === "suppliers" && <>
                <Field label="Name*"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Contact"><Input value={form.contact_person||""} onChange={e=>setForm({...form,contact_person:e.target.value})} /></Field>
                  <Field label="Phone"><Input value={form.phone||""} onChange={e=>setForm({...form,phone:e.target.value})} /></Field>
                  <Field label="Email"><Input value={form.email||""} onChange={e=>setForm({...form,email:e.target.value})} /></Field>
                  <Field label="GST"><Input value={form.gst_number||""} onChange={e=>setForm({...form,gst_number:e.target.value})} /></Field>
                </div>
                <Field label="Address"><Input value={form.address||""} onChange={e=>setForm({...form,address:e.target.value})} /></Field>
              </>}
              {tab === "dpis" && <>
                <Field label="DPI Value*"><Input type="number" min="1" value={form.value} onChange={e=>setForm({...form,value:parseInt(e.target.value||0)})} data-testid="dpi-form-value" /></Field>
                <div className="text-xs text-zinc-500">Typical DPI range: 60 – 200. Deactivate DPI values you no longer use so they hide from the New Job form.</div>
              </>}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.active} onChange={e=>setForm({...form,active:e.target.checked})} /> Active
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <Btn variant="outline" onClick={() => setShowForm(false)}>Cancel</Btn>
              <Btn onClick={save} data-testid="masters-save-btn">Save</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
